/**
 * Plain sentences for the decision screens (the desk hero, the phone card, the blocking box).
 *
 * Everything here is text only. It reads the saved checkpoint, the top candidate and the
 * viewer-readable ENTER state from GET /health (`health.enter`); it never evaluates a rule.
 * Relative imports only, so these helpers run under the plain lib unit tests.
 */
import { RUN_META, checkpointSchedule } from "./format";
import { ruleMeaning } from "./ruleMeanings";

/** "no_trade" / "NO-TRADE" → "NO TRADE" (same rule as the design system's normDecision). */
export function normLabel(v) {
  if (!v) return "NO TRADE";
  const s = String(v).toUpperCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  return s === "NOTRADE" ? "NO TRADE" : s;
}

const isEnterLabel = (d) => d === "ENTER" || d === "ENTER PRE OPEN" || d === "ENTER PRE-OPEN";

/** The ENTER switch cannot print today. `undefined` when the backend did not send the state. */
export function enterIsOff(enter) {
  if (!enter || typeof enter !== "object" || typeof enter.in_effect !== "boolean") return undefined;
  return enter.in_effect === false;
}

/**
 * The permanent line shown under the decision when ENTER cannot print. Null when ENTER is in
 * effect or when the backend did not send the state (an older backend): say nothing rather than
 * something wrong.
 */
export function enterOffLine(enter) {
  if (enterIsOff(enter) !== true) return null;
  const tail = "Even a setup that passes every rule shows as WAIT or NO TRADE.";
  if (enter.enabled === false) return `ENTER is switched off by the owner (edge not proven). ${tail}`;
  const reason = String(enter.reason || "").trim().replace(/\.$/, "");
  return `${reason || "ENTER is switched on but not in effect"}. ${tail}`;
}

/** The candidate passed every rule, including the entry gate (no FAIL in any family). */
export function passedAllRules(candidate) {
  if (!candidate) return false;
  if (candidate.gate_result !== "PASS_LETC") return false;
  if (candidate.n_fail_total != null) return Number(candidate.n_fail_total) === 0;
  return candidate.rules?.B2 === "PASS" || candidate.rules?.B1 === "PASS";
}

/**
 * The label is WAIT although every rule passed. The engine only produces this when the ENTER
 * switch (live_chain) was off or the kill switch was armed: a WAIT caused by a failed rule
 * always records that rule as `blocking_rule`. The candidate, when loaded, confirms it.
 */
export function enterOffPassed({ decision, checkpoint, candidate }) {
  const d = normLabel(decision);
  if (d !== "WAIT") return false;
  if (checkpoint?.blocking_rule) return false;
  if (candidate && !passedAllRules(candidate)) return false;
  return true;
}

/** The next scheduled run today that has not run yet (1–6), or null for another day. */
export function nextScheduledRun(date, doneRuns, now = new Date(), today = true) {
  if (!today || !date) return null;
  const done = doneRuns instanceof Set ? doneRuns : new Set(doneRuns || []);
  const t = now instanceof Date ? now.getTime() : Number(now);
  const next = [1, 2, 3, 4, 5, 6]
    .map((run) => ({ run, ...(checkpointSchedule(date, RUN_META[run].time) || {}) }))
    .find((s) => s.at && s.at > t && !done.has(s.run));
  return next ? next.run : null;
}

/** "R4 09:46 ET" for a run number. */
export const runLabel = (run) => (run && RUN_META[run] ? `R${run} ${RUN_META[run].time} ET` : null);

/** The sentence about what happens next for a WAIT. Run 6 makes no decision. */
function nextRunSentence({ run, nextRun, nextLabel, today }) {
  const last = nextRun === 6 || Number(run) === 5;
  if (last) return today === false ? "This was the last decision run of the day; no trade that day." : "Last decision run of the day; no trade today.";
  const label = nextLabel || runLabel(nextRun);
  return `Re-checked at the next run${label ? ` (${label})` : ""}.`;
}

/** "Not enough market indicators agreed on the direction (rule C2)." */
export function ruleSentence(rule) {
  if (!rule) return "";
  const meaning = ruleMeaning(rule);
  return meaning ? `${meaning.replace(/\.$/, "")} (rule ${rule}).` : `Rule ${rule} was not met.`;
}

/**
 * One plain-language sentence for the decision hero and the phone card.
 *
 * `nextRun` is the number of the next scheduled run today (from the shell's session state or
 * nextScheduledRun); `nextLabel` is an already formatted label and wins when given. `candidate`
 * is the top (rank 1) candidate when loaded. `enter` is health.enter. `today` is false for an
 * archived session.
 */
export function decisionReason({ decision, checkpoint, candidate, killArmed, killReason, killBy, nextRun, nextLabel, enter, today }) {
  const d = normLabel(decision);
  const rule = checkpoint?.blocking_rule;
  if (killArmed && d === "NO TRADE") {
    return `Forced by the kill switch${killBy ? ` (armed by ${killBy})` : ""}${killReason ? ` · ${killReason}` : ""}.`;
  }
  let base;
  if (d === "NO TRADE") {
    base = rule ? `No candidate passed every rule. ${ruleSentence(rule)}` : "The gate is closed.";
  } else if (d === "ENTER") {
    base = "The primary passed every rule. You place the order yourself; Sentinel sends none.";
  } else if (isEnterLabel(d)) {
    base = "Qualifies before the open if the official open confirms. You place the order yourself.";
  } else if (d === "WAIT") {
    const next = nextRunSentence({ run: checkpoint?.run, nextRun, nextLabel, today });
    if (enterOffPassed({ decision: d, checkpoint, candidate })) {
      if (killArmed || checkpoint?.operational_status === "KILL SWITCH") return `All rules passed, but the kill switch is armed, so ENTER is refused. ${next}`;
      return `All rules passed, but ENTER is switched off. ${next}`;
    }
    base = `Close to qualifying.${rule ? ` ${ruleSentence(rule)}` : ""} ${next}`;
  } else if (d === "WAIT FOR OPEN") {
    base = "Nothing to act on until the 09:33 official-open reset.";
  } else if (d === "PROVISIONAL") {
    base = "Pre-market build. Not actionable; it will be re-run.";
  } else {
    base = "";
  }
  if (!base) return base;
  return killArmed ? `${base} Kill switch armed: ENTER refused.` : base;
}

/** The text for the "no blocking rule" box when a WAIT has no blocking rule. */
export function noBlockingRuleText({ decision, checkpoint, candidate, enter }) {
  if (enterOffPassed({ decision, checkpoint, candidate })) {
    const kill = checkpoint?.operational_status === "KILL SWITCH";
    return kill ? "All rules passed, but the kill switch is armed." : "All rules passed, but ENTER is switched off.";
  }
  const safety = (checkpoint?.safety_blocks || []).length > 0;
  return `No single blocking rule recorded${safety ? " · see the safety blocks above" : ""}.`;
}

/** A safety block as a plain sentence, falling back to the reason the runner recorded. */
export function safetyBlockText(block) {
  if (!block) return "";
  return ruleMeaning(block.code) || block.reason || String(block.code || "");
}

/** The reason the runner recorded when it adds something the plain sentence does not say. */
export function safetyBlockDetail(block) {
  if (!block || !block.reason) return null;
  const meaning = ruleMeaning(block.code);
  if (!meaning) return null;
  return block.reason;
}
